import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Merge class names with Tailwind CSS conflict resolution
 */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

/**
 * Format a number as USD currency
 */
export function formatUsd(amount: number): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount)
}

/**
 * Format a number as compact USD currency (e.g. $1.2m)
 */
export function formatCompactUsd(value: number): string {
	if (value >= 1_000_000) {
		return `$${(value / 1_000_000).toFixed(1)}m`
	}
	if (value >= 1_000) {
		return `$${(value / 1_000).toFixed(1)}k`
	}
	return `$${Math.round(value)}`
}

/**
 * Format a number in compact notation (e.g. 1.2K, 3.4M)
 */
export function formatCompactNumber(value: number): string {
	if (value >= 1_000_000) {
		return `${(value / 1_000_000).toFixed(1)}M`
	}
	if (value >= 1_000) {
		return `${(value / 1_000).toFixed(1)}K`
	}
	return String(value)
}

/**
 * Format shares for display with intelligent precision based on magnitude.
 *
 * Rules:
 * - < 0.01: "<0.01" (very small amounts)
 * - < 1: 2 decimals (0.13, 0.26)
 * - 1 - 99.9: 1 decimal, trailing zeros removed (1.5, 25)
 * - 100 - 999: integer (150, 999)
 * - >= 1000: compact with 1 decimal (1.5k, 2.3m)
 */
export function formatShares(shares: number): string {
	if (shares < 0.01) {
		return "<0.01"
	}
	if (shares < 1) {
		// 2 decimals for sub-1 values, remove trailing zeros
		const formatted = shares.toFixed(2)
		return formatted.replace(/\.?0+$/, "") || "0"
	}
	if (shares < 100) {
		// 1 decimal, remove trailing zeros, use locale for thousands separator
		const rounded = Math.round(shares * 10) / 10
		if (rounded === Math.floor(rounded)) {
			return rounded.toLocaleString("en-US")
		}
		return rounded.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
	}
	if (shares < 1000) {
		// Integer for 100-999
		return Math.round(shares).toLocaleString("en-US")
	}
	// Compact notation for 1000+
	if (shares >= 1_000_000) {
		const value = shares / 1_000_000
		const formatted = value < 10 ? value.toFixed(1) : Math.round(value).toString()
		return `${formatted.replace(/\.0$/, "")}m`
	}
	// 1k - 999k
	const value = shares / 1000
	const formatted = value < 10 ? value.toFixed(1) : Math.round(value).toString()
	return `${formatted.replace(/\.0$/, "")}k`
}

/**
 * Shorten an address for display (e.g., 0x1234...5678)
 */
export function shortenAddress(address: string, chars = 4): string {
	if (address.length < chars * 2 + 2) {
		return address
	}
	return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`
}

/**
 * Format time remaining as a human-readable string
 * Shows seconds when under 10 minutes for urgency
 */
export function formatTimeRemaining(targetDate: string | Date): string {
	const target = typeof targetDate === "string" ? new Date(targetDate) : targetDate
	const now = new Date()
	const total = Math.max(0, target.getTime() - now.getTime())

	if (total === 0) {
		return "Ended"
	}

	const days = Math.floor(total / (1000 * 60 * 60 * 24))
	const hours = Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
	const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60))
	const seconds = Math.floor((total % (1000 * 60)) / 1000)

	if (days > 0) {
		return `${days}d ${hours}h`
	}
	if (hours > 0) {
		return `${hours}h ${minutes}m`
	}
	// Under 10 minutes: show seconds for urgency
	if (minutes < 10) {
		return `${minutes}m ${seconds}s`
	}
	return `${minutes}m`
}

/**
 * Get optimal update interval based on time remaining
 * - > 1 hour: update every minute
 * - < 1 hour: update every 30 seconds
 * - < 10 minutes: update every second
 */
export function getCountdownInterval(targetMs: number): number {
	const remaining = targetMs - Date.now()
	if (remaining <= 0) {
		return 60_000 // Ended, slow updates
	}
	if (remaining < 10 * 60 * 1000) {
		return 1_000 // Under 10 min: every second
	}
	if (remaining < 60 * 60 * 1000) {
		return 30_000 // Under 1 hour: every 30s
	}
	return 60_000 // Over 1 hour: every minute
}
